// =============================================================================
// Optim TURPE V2 — LE LEVIER « COMPLÉTER » (DEV-1913, L29, A18).
//
// Décision de Mathieu du 23/09 : « si l'utilisateur veut compléter des
// informations absentes il doit pouvoir ». Une information manquante n'est donc
// plus un cul-de-sac : la cause servie par le moteur nomme le champ, l'écran
// ouvre ce champ dans « Affiner l'étude », la saisie part comme surcharge tracée
// et l'étude se relance.
//
// Ce module ne fait que la CORRESPONDANCE : cause servie (ou entrée du registre)
// → ancre du formulaire. Il n'invente aucune cause, n'écrit rien, et rend `null`
// dès qu'il ne sait pas — ce qui laisse l'écran dire qu'il ne sait pas.
//
// Deux familles, et la seconde compte autant que la première :
// 1. Les causes dont le champ EXISTE côté moteur (`ANCRE_PAR_CAUSE`) : le geste
//    « Compléter » ouvre le champ.
// 2. Les causes dont le champ N'EXISTE PAS encore (`SAISIE_NON_SERVIE`) : segment,
//    maxima de puissance par phase. On le DIT au client au
//    lieu de lui proposer un geste qui ne mènerait nulle part. Ces trois besoins
//    sont portés au moteur et à la collecte, ils ne sont pas traités ici.
// =============================================================================

import type { EchecLu } from "@modules/courtage/utils/optim-turpe-v2.echec"
import type { EntreeLue } from "@modules/courtage/utils/optim-turpe-v2.registre"

/**
 * Les endroits du formulaire « Affiner l'étude » qu'un geste « Compléter » peut
 * ouvrir. Une ancre est un point d'ENTRÉE d'écran, pas un chemin de donnée : le
 * chemin, lui, vit dans `ANCRE_PAR_CHAMP`.
 */
export type AncreAffiner =
    | "periode"
    | "structure"
    | "raccordement"
    | "heures-creuses"
    // DEV-2176 : les entrées ouvertes à la saisie le 25/09/2026.
    | "pointe-fixe"
    | "bareme"
    | "talon"
    | "branchement"
    // DEV-2176 : le segment, saisissable depuis le 28/09/2026.
    | "segment"
    // Q27 (14/10/2026) : « Chiffrer d'autres puissances » ouvre la saisie des
    // puissances à chiffrer (objectif « le coût des puissances de mon choix »).
    | "chiffrer"

/**
 * Le champ du registre des entrées → l'ancre qui l'ouvre.
 *
 * Seuls figurent ici les chemins que le moteur accepte réellement en surcharge
 * (`InputsModel`, lot DEV-1909). Un chemin absent de cette table n'est pas
 * saisissable depuis la page, et l'écran ne propose alors aucun geste.
 */
export const ANCRE_PAR_CHAMP: Record<string, AncreAffiner> = {
    "structure_tarifaire.formule_tarifaire_acheminement.fta": "structure",
    "structure_tarifaire.puissance_souscrite_soutirage.puissance": "structure",
    "structure_tarifaire.puissance_souscrite_soutirage.denivele": "structure",
    "structure_tarifaire.offre_tarifaire.option_tarifaire": "structure",
    "compteur.raccordement.puissance_raccordement_soutirage": "raccordement",
    "comptage.plages_horaires.heures_creuses": "heures-creuses",
    "comptage.plages_horaires.heures_pointe_fixe": "pointe-fixe",
    "structure_tarifaire.offre_tarifaire.turpe.date": "bareme",
    "optimisation.talon_minimum": "talon",
    "compteur.raccordement.branchement.triphase": "branchement",
    "compteur.segment": "segment",
    "data.date_from": "periode",
    "data.date_to": "periode",
}

/**
 * Le code de refus servi par le moteur (`mixins/runerrors.json`) → l'ancre du
 * champ à compléter. Les codes sont recopiés du moteur, jamais devinés.
 */
export const ANCRE_PAR_CAUSE: Record<string, AncreAffiner> = {
    // La formule tarifaire d'acheminement et la structure de puissance.
    "validation-fta-manquante": "structure",
    "validation-fta-non-reconnue": "structure",
    "validation-fta-incompatible-segment-source": "structure",
    // La formule transmise par Enedis contredit le segment : la saisir
    // (courtage v183, `runerrors.json`).
    "donnee-fta-source-incompatible-segment": "structure",
    "validation-denivele-ou-puissance": "structure",
    "param-puissance-manquante-btinf": "structure",
    "param-pointe-manquante-c2": "structure",
    "param-pointe-manquante-fta-ht": "structure",
    "scenario-hors-segment-c4": "structure",
    "scenario-denivele-non-croissant": "structure",
    "validation-cible-manquante": "structure",
    // Le segment saisi, refusé contre la formule tarifaire (DEV-2176).
    "segment-incoherent-avec-fta": "segment",
    // Le raccordement.
    "param-raccordement-unite-inconnue": "raccordement",
    "param-raccordement-invraisemblable": "raccordement",
    "validation-plancher-hors-segment": "raccordement",
    // Les plages horaires.
    "param-plages-heures-creuses-manquantes": "heures-creuses",
    "param-option-tarifaire-plages-pointe-fixe-manquantes": "heures-creuses",
    "validation-plage-pointe-fixe-duree-incorrecte": "heures-creuses",
    // La période analysée. « Une classe tarifaire n'a aucune mesure sur la
    // période » se corrige en élargissant la fenêtre, pas en fabriquant un
    // chiffre pour la classe vide (L29 : une absence n'est jamais un chiffre).
    "donnee-plage-sans-puissance": "periode",
    "donnee-aucune-disponible": "periode",
    "donnee-couverture-insuffisante": "periode",
    "donnee-trous-present": "periode",
    "validation-fenetre-invalide": "periode",
    "validation-fenetre-trop-longue": "periode",
    "collecte-interrompue": "periode",
    // DEV-2163 : la période n'offre pas les classes tarifaires du segment.
    "donnee-classe-hors-fenetre": "periode",
}

/**
 * Le levier NOMMÉ par le moteur (`error.levier`) → l'ancre. Il passe avant le
 * code : quand le moteur désigne lui-même le levier, c'est lui qui a raison.
 */
export const ANCRE_PAR_LEVIER: Record<string, AncreAffiner> = {
    fta: "structure",
    denivele: "structure",
    dénivelé: "structure",
    scenario: "structure",
    scénario: "structure",
    fenetre: "periode",
    fenêtre: "periode",
    // Levier servi par le refus `donnee-classe-hors-fenetre` (relevé le 25/09).
    periode: "periode",
    période: "periode",
    raccordement: "raccordement",
    "plancher de modulation, raccordement": "raccordement",
    "heures creuses": "heures-creuses",
}

/**
 * Les absences que le client NE PEUT PAS encore compléter, parce que le moteur
 * n'expose aucun champ d'entrée pour elles (`InputsModel`, DEV-1909).
 *
 * On le dit au lieu de proposer un geste creux. Chaque ligne est un BESOIN porté
 * hors de cette page : le segment est dérivé du raccordement Enedis et ignoré
 * s'il est envoyé ; le branchement triphasé et les maxima de puissance par phase
 * (PMA 1/2/3, décision L20) relèvent de la collecte et du moteur.
 */
export const SAISIE_NON_SERVIE: Record<string, string> = {
    "param-segment-manquant":
        "Le segment tarifaire de ce compteur n'est pas connu. Il est dérivé du raccordement chez Enedis et ne se saisit pas ici.",
    "segment-source-indetermine":
        "Le segment tarifaire de ce compteur n'est pas connu. Il est dérivé du raccordement chez Enedis et ne se saisit pas ici.",
    "validation-segment-non-supporte":
        "Le segment tarifaire de ce compteur n'est pas connu. Il est dérivé du raccordement chez Enedis et ne se saisit pas ici.",
    "param-puissance-max-c5-manquante":
        "Enedis n'a pas transmis les puissances maximales atteintes de ce compteur. Elles ne se saisissent pas ici.",
    "param-bareme-anterieur-au-plancher":
        "La date de barème demandée précède la seule grille TURPE que l'étude sait calculer. Choisissez une date postérieure au 1er août 2026.",
}

/**
 * L'ancre à ouvrir après un refus, ou `null` quand la page ne sait pas où mener.
 *
 * Le levier servi passe avant le code, et `aucun` reste une réponse pleine : il
 * n'y a rien à compléter ici, on ne propose donc pas de geste.
 */
export function ancreDepuisEchec(echec: EchecLu | null): AncreAffiner | null {
    if (!echec) return null
    if (echec.levier === "aucun") return null
    if (echec.levier && ANCRE_PAR_LEVIER[echec.levier]) {
        return ANCRE_PAR_LEVIER[echec.levier]
    }
    if (echec.code && ANCRE_PAR_CAUSE[echec.code]) {
        return ANCRE_PAR_CAUSE[echec.code]
    }
    return null
}

/** L'ancre qui ouvre le champ d'une entrée du registre, ou `null`. */
export function ancreDepuisChamp(
    champ: string | null | undefined
): AncreAffiner | null {
    if (!champ) return null
    return ANCRE_PAR_CHAMP[champ] ?? null
}

/**
 * La phrase à dire quand la complétion n'est pas servie, ou `null`.
 *
 * Accepte aussi bien un code de refus qu'un chemin d'entrée : les deux désignent
 * la même absence vue de deux endroits de l'écran.
 */
export function saisieNonServie(
    codeOuChamp: string | null | undefined
): string | null {
    if (!codeOuChamp) return null
    return SAISIE_NON_SERVIE[codeOuChamp] ?? null
}

/**
 * Cette entrée du registre mérite-t-elle un geste « Compléter » ?
 *
 * Oui quand elle est absente ou vide, qu'elle est déclarée surchargeable par le
 * moteur, et que la page sait où la saisir. Une valeur déjà renseignée reste
 * corrigeable par « Affiner l'étude » : on ne double pas le geste sur chaque
 * ligne, le registre deviendrait une forêt de boutons.
 */
export function peutCompleter(entree: EntreeLue): boolean {
    if (!entree.surchargeable) return false
    // Une date de barème vide n'empêche rien : le barème du jour s'applique.
    // Elle reste corrigeable dans « Affiner l'étude », sans geste sur sa ligne.
    if (entree.champ === "structure_tarifaire.offre_tarifaire.turpe.date") {
        return false
    }
    if (ancreDepuisChamp(entree.champ) === null) return false
    const vide =
        entree.valeur === null ||
        entree.valeur === undefined ||
        entree.valeur === "" ||
        (Array.isArray(entree.valeur) && entree.valeur.length === 0)
    return entree.statut === "absente" || vide
}

/**
 * Revue témoin du 15/10/2026 (D17) : les refus dont la valeur manquante ne se
 * saisit pas (`SAISIE_NON_SERVIE`, pas de geste « Compléter »), mais dont le
 * remède écrit renvoie à un autre champ de la page. Sans cette table, le
 * formulaire s'ouvrait sur un refus « à levier » sans aucun champ montré.
 */
export const ANCRE_DU_REMEDE_HORS_SAISIE: Record<string, AncreAffiner> = {
    // Aucune puissance maximale transmise sur la période : une autre période
    // peut en porter (remède de `echec.ts`).
    "param-puissance-max-c5-manquante": "periode",
}

/**
 * Parcours guidé (07/10/2026) : les SEULS paramètres qui lèvent un refus, lus
 * sur le remède servi (levier puis code, `ancreDepuisEchec`). Un segment
 * refusé contre la formule se corrige par l'un ou l'autre : les deux champs
 * sont montrés. Sans remède servi, aucun paramètre n'est proposé : la page
 * garde « Modifier les paramètres » pour tout le reste.
 */
export function remedesDuRefus(echec: EchecLu | null): AncreAffiner[] {
    const ancre =
        ancreDepuisEchec(echec) ??
        (echec?.code ? (ANCRE_DU_REMEDE_HORS_SAISIE[echec.code] ?? null) : null)
    if (!ancre) return []
    if (ancre === "segment") return ["segment", "structure"]
    if (ancre === "heures-creuses") return ["heures-creuses", "pointe-fixe"]
    return [ancre]
}
